import { useState, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../../../../firebase';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';
import type { ApplicationData, DocumentInfo } from '../types';

interface UseApplicationEditReturn {
  isEditing: boolean;
  editedData: Partial<ApplicationData>;
  newDocuments: {[key: string]: DocumentInfo};
  isSubmitting: boolean;
  handleEdit: () => void;
  handleCancelEdit: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, type: string) => void;
  handleDeleteFile: (type: string) => void;
  handleSubmit: () => Promise<void>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

export const useApplicationEdit = (
  application: ApplicationData | null,
  setApplication: React.Dispatch<React.SetStateAction<ApplicationData | null>>,
  setDocumentUrls: React.Dispatch<React.SetStateAction<{[key: string]: DocumentInfo}>>,
  documentUrls: {[key: string]: DocumentInfo}
): UseApplicationEditReturn => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Partial<ApplicationData>>({});
  const [newDocuments, setNewDocuments] = useState<{[key: string]: DocumentInfo}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setEditedData({
      name: application?.name,
      phoneNumber: application?.phoneNumber,
      organization: application?.organization,
      additionalRequest: application?.additionalRequest
    });
  }, [application]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedData({});
    setNewDocuments({});
    setError(null);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 크기 검증 (최대 5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      setError(`파일 크기는 5MB를 초과할 수 없습니다. (현재: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      e.target.value = ''; // input 초기화
      return;
    }

    // 파일 타입 검증 (MIME 타입 확인)
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ];
    if (!allowedTypes.includes(file.type)) {
      setError(`허용되지 않는 파일 형식입니다. PDF, JPG, PNG 파일만 업로드할 수 있습니다. (현재: ${file.type})`);
      e.target.value = ''; // input 초기화
      return;
    }

    // 파일 확장자 검증
    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
      setError('허용되지 않는 파일 확장자입니다. .pdf, .jpg, .jpeg, .png 파일만 업로드할 수 있습니다.');
      e.target.value = ''; // input 초기화
      return;
    }

    // 검증 통과 시 상태 업데이트
    setError(null); // 이전 에러 초기화
    setNewDocuments(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        file,
        originalFileName: file.name,
        storagePath: documentUrls[type]?.storagePath || ''
      }
    }));
  }, [documentUrls]);

  const handleDeleteFile = useCallback((type: string) => {
    // 필수 제출서류는 삭제할 수 없음
    if (type === 'identification' || type === 'employment') {
      return;
    }
    setNewDocuments(prev => {
      const newDocs = { ...prev };
      delete newDocs[type];
      return newDocs;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!application) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const applicationRef = doc(db, 'portville_applications', application.id);
      const now = new Date();

      // 새로운 파일 업로드
      const updatedDocuments = { ...application.documents };
      for (const [type, docInfo] of Object.entries(newDocuments)) {
        if (docInfo.file) {
          // 기존 파일 삭제
          if (updatedDocuments[type]) {
            try {
              const oldFileRef = ref(storage, updatedDocuments[type]!);
              await deleteObject(oldFileRef);
            } catch (err) {
              console.error('기존 파일 삭제 실패:', err);
            }
          }

          // 새 파일 업로드
          const fileExt = docInfo.file.name.split('.').pop() || 'pdf';
          const newFileName = `${application.uid}_${type}_${now.getTime()}.${fileExt}`;
          const newPath = `waiting_documents/${application.uid}/submitted/${newFileName}`;
          const newFileRef = ref(storage, newPath);

          await uploadBytes(newFileRef, docInfo.file);
          updatedDocuments[type] = newPath;
        }
      }

      // Firestore 문서 업데이트
      await updateDoc(applicationRef, {
        ...editedData,
        documents: updatedDocuments,
        updatedAt: now
      });

      // 상태 업데이트
      setApplication(prev => prev ? {
        ...prev,
        ...editedData,
        documents: updatedDocuments,
        updatedAt: now
      } : null);

      // 문서 URL 새로고침
      const urls: {[key: string]: DocumentInfo} = {};
      for (const [docType, path] of Object.entries(updatedDocuments)) {
        if (path) {
          try {
            const fileRef = ref(storage, path);
            const url = await getDownloadURL(fileRef);
            const fileName = path.split("/").pop() || "";
            const originalFileName = fileName.split("_").slice(3).join("_") || fileName;

            urls[docType] = {
              url,
              originalFileName,
              storagePath: path
            };
          } catch (err) {
            console.error(`${docType} 문서 URL 가져오기 실패:`, err);
          }
        }
      }
      setDocumentUrls(urls);

      setIsEditing(false);
      setEditedData({});
      setNewDocuments({});
    } catch (err) {
      console.error('신청 내역 수정 중 오류 발생:', err);
      setError('신청 내역 수정 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  }, [application, editedData, newDocuments, setApplication, setDocumentUrls]);

  return {
    isEditing,
    editedData,
    newDocuments,
    isSubmitting,
    handleEdit,
    handleCancelEdit,
    handleInputChange,
    handleFileChange,
    handleDeleteFile,
    handleSubmit,
    error,
    setError
  };
};
